"use client";

import { Menu as BaseMenu } from "@base-ui/react/menu";
import { useLayoutEffect, useState, type ComponentProps, type ReactNode } from "react";
import { Tooltip, useHasTooltip } from "./tooltip";
import { cn } from "../lib/utils";
import { useKitseed } from "../lib/provider";
import {
  choicePopupClassName,
  choiceItemClassName,
  choiceRowClassName,
  choiceLabelClassName,
} from "./picker-styles";

export const MenuRoot = BaseMenu.Root;
export const MenuGroup = BaseMenu.Group;
export const MenuRadioGroup = BaseMenu.RadioGroup;
export const MenuRadioItemIndicator = BaseMenu.RadioItemIndicator;
export const MenuSubmenuRoot = BaseMenu.SubmenuRoot;

export function MenuTrigger({
  tooltip,
  ...props
}: ComponentProps<typeof BaseMenu.Trigger> & { tooltip?: ReactNode }) {
  const hasTooltip = useHasTooltip();
  const element = <BaseMenu.Trigger {...props} />;
  return tooltip && !hasTooltip ? <Tooltip content={tooltip}>{element}</Tooltip> : element;
}

export function MenuContent({
  className,
  side = "bottom",
  align = "start",
  sideOffset = 6,
  children,
  anchor,
  positionMethod = "fixed",
  collisionAvoidance,
  positionerStyle,
  style,
  unstyled = false,
  normalizeZoom = true,
  ...props
}: ComponentProps<typeof BaseMenu.Popup> & {
  unstyled?: boolean;
  /** Disable when the host owns popup coordinate normalization. */
  normalizeZoom?: boolean;
  anchor?: ComponentProps<typeof BaseMenu.Positioner>["anchor"];
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  positionMethod?: ComponentProps<typeof BaseMenu.Positioner>["positionMethod"];
  collisionAvoidance?: ComponentProps<typeof BaseMenu.Positioner>["collisionAvoidance"];
  positionerStyle?: ComponentProps<typeof BaseMenu.Positioner>["style"];
}) {
  const { density } = useKitseed();
  const [positioner, setPositioner] = useState<HTMLDivElement | null>(null);
  const [pageScale, setPageScale] = useState(1);
  useLayoutEffect(() => {
    if (!normalizeZoom || !positioner) return;
    const root = document.documentElement;
    const body = document.body;
    const measure = () =>
      setPageScale(
        (Number.parseFloat(getComputedStyle(root).zoom) || 1) *
          (Number.parseFloat(getComputedStyle(body).zoom) || 1),
      );
    measure();
    // Observe only mounted popups. Closed row menus should not each observe the document.
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(body);
    return () => observer.disconnect();
  }, [positioner, normalizeZoom]);
  return (
    <BaseMenu.Portal
      style={
        normalizeZoom && positionMethod === "absolute"
          ? { zoom: 1 / pageScale, position: "relative" }
          : undefined
      }
    >
      <BaseMenu.Positioner
        ref={setPositioner}
        anchor={anchor}
        positionMethod={positionMethod}
        collisionAvoidance={collisionAvoidance}
        style={
          normalizeZoom
            ? (state) => ({
                // Fixed uses viewport pixels. Absolute needs its offset parent normalized as well.
                zoom: positionMethod === "absolute" ? 1 : 1 / pageScale,
                "--menu-page-scale": pageScale,
                ...(typeof positionerStyle === "function"
                  ? positionerStyle(state)
                  : positionerStyle),
              })
            : positionerStyle
        }
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className="z-50"
      >
        <BaseMenu.Popup
          data-density={unstyled ? undefined : density}
          {...props}
          style={
            normalizeZoom
              ? (state) => ({
                  zoom: pageScale,
                  ...(typeof style === "function" ? style(state) : style),
                })
              : style
          }
          className={(state) =>
            cn(
              !unstyled && choicePopupClassName,
              normalizeZoom &&
                "max-w-[calc((100vw-1rem)/var(--menu-page-scale))] max-h-[calc(var(--available-height)/var(--menu-page-scale))]",
              typeof className === "function" ? className(state) : className,
            )
          }
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

type ItemHint = { tooltip?: ReactNode; disabledReason?: string | undefined; unstyled?: boolean };
export function MenuItem({
  className,
  tooltip,
  disabledReason,
  unstyled = false,
  ...props
}: ComponentProps<typeof BaseMenu.Item> & ItemHint) {
  const hasTooltip = useHasTooltip();
  const content = props.disabled && disabledReason ? disabledReason : tooltip;
  const element = (
    <BaseMenu.Item
      {...props}
      data-tooltip-disabled-reason={props.disabled ? disabledReason : undefined}
      className={(state) =>
        cn(
          !unstyled && choiceItemClassName,
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
  return content && !hasTooltip ? <Tooltip content={content}>{element}</Tooltip> : element;
}

export function MenuSubmenuTrigger({
  className,
  unstyled = false,
  ...props
}: ComponentProps<typeof BaseMenu.SubmenuTrigger> & { unstyled?: boolean }) {
  return (
    <BaseMenu.SubmenuTrigger
      {...props}
      className={(state) =>
        cn(
          !unstyled && choiceItemClassName,
          !unstyled && "data-popup-open:bg-hover",
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
}

export function MenuRadioItem({
  className,
  tooltip,
  disabledReason,
  unstyled = false,
  ...props
}: ComponentProps<typeof BaseMenu.RadioItem> & ItemHint) {
  const hasTooltip = useHasTooltip();
  const content = props.disabled && disabledReason ? disabledReason : tooltip;
  const element = (
    <BaseMenu.RadioItem
      {...props}
      data-tooltip-disabled-reason={props.disabled ? disabledReason : undefined}
      className={(state) =>
        cn(
          !unstyled && choiceItemClassName,
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
  return content && !hasTooltip ? <Tooltip content={content}>{element}</Tooltip> : element;
}

export function MenuDangerItem({
  className,
  ...props
}: ComponentProps<typeof BaseMenu.Item> & ItemHint) {
  return (
    <MenuItem
      {...props}
      className={(state) =>
        cn(
          "text-danger-text data-highlighted:bg-danger-subtle [&_svg]:text-danger-text",
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
}

export function MenuLabel({
  className,
  unstyled = false,
  ...props
}: ComponentProps<typeof BaseMenu.GroupLabel> & { unstyled?: boolean }) {
  return (
    <BaseMenu.GroupLabel
      {...props}
      className={(state) =>
        cn(
          !unstyled && choiceLabelClassName,
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
}

export function MenuSeparator({ className }: { className?: string }) {
  return <BaseMenu.Separator className={cn("-mx-1 my-1 h-px bg-line", className)} />;
}

export function MenuCheckboxItem({
  className,
  tooltip,
  disabledReason,
  unstyled = false,
  ...props
}: ComponentProps<typeof BaseMenu.CheckboxItem> & ItemHint) {
  const hasTooltip = useHasTooltip();
  const content = props.disabled && disabledReason ? disabledReason : tooltip;
  const element = (
    <BaseMenu.CheckboxItem
      {...props}
      data-tooltip-disabled-reason={props.disabled ? disabledReason : undefined}
      className={(state) =>
        cn(
          !unstyled && choiceRowClassName,
          typeof className === "function" ? className(state) : className,
        )
      }
    />
  );
  return content && !hasTooltip ? <Tooltip content={content}>{element}</Tooltip> : element;
}
